// ── CompactOutcomeBands — the Compact presentation for GameDetail's
//    line-less market sections (#105) ────────────────────────────────
//
// Moneyline and BTTS are a list of NAMED OUTCOMES, each carrying up to two
// venue books, with NO scale between them. They get ONE dense band EACH, no
// selector — they're what you're comparing, so hiding all but one behind a chip
// strip would be the opposite of useful (Brendan, #105).
//
// A value LADDER is different: a player+stat's "1+/2+/3+", or a team's runs
// ladder, is NOT a set of distinct outcomes — its rungs differ only by a
// numeric threshold, i.e. they ARE points on a scale. Those do collapse behind
// a strip, but NOT here: they render as one row of the section's normal TABLE
// (ladderRows.ts), because the density trade this component makes — dropping
// the analytics columns — is wrong for them. #141 briefly routed them through
// here; that cost Player Props and Events their Odds / Vol / Liq / Q / Δ
// columns, which compact Spread/Total keeps (Dan, 2026-07-24).
//
// Density comes from dropping the detailed table's analytics columns (Odds /
// Vol / Liq / Q / Δ-per-minute) and stacking identity above prices, which is
// what finally fits BOTH venues side by side at 1470px.
//
// Everything the ticket requires to survive a density switch does:
//   • identity  — the outcome label, plus the SETTLED chip
//   • venue     — explicit K / P groups, never merged or best-of
//   • side      — Y and N pills, both venues, always both rendered
//   • price     — via the SAME PricePill + pillPriceCents the table uses
//   • status    — settled / final / no-book states render truthfully
//   • actions   — pill click trades, header click opens the orderbook
//
// Presentation only: this component owns no market, pricing or order state.
// The parent supplies the outcomes and owns expand + trade routing, exactly as
// CompactLinesTable is controlled by its two sections.

import { cn } from '@/lib/infra/utils';
import { feeDisplayCanHide } from '@/components/molecules/FeeScheduleMarker';
import { useLt2Access } from '@/hooks/useLt2Access';
import { useLt2Card } from '@/hooks/useLt2Card';
import { Lt2FocusBadge, Lt2FocusGhostChip } from '@/components/Lt2FocusBadge';
import { useOneClickStore } from '@/stores/oneClickStore';
import { useFeeDisplayStatus, useFeeFor } from '@/services/hydraFees';
import { useDisplaySettings } from '@/stores/displaySettings';
import { useLt2FocusStore } from '@/stores/lt2FocusStore';
import {
  lt2MoneylineGameCardId,
  lt2MoneylineGameTargets,
  type Lt2MoneylineCardScope,
} from '../ValueDashboard/lt2Moneyline';
import { PosRing } from '../ValueDashboard/ladder/PosRing';
import { heldContractsTitle } from '../ValueDashboard/ladder/heldContractsTitle';
import { PricePill } from '../ValueDashboard/ladder/PricePill';
import {
  crossedBookRead,
  formatVolume,
  orderTooltip,
  pillPriceCents,
  type BidRowOrder,
} from '../ValueDashboard/ladder/helpers';
import { buildCompactLadderSide, buildCompactOrdersByCents } from './compactLadder';
import {
  CancelLevelControl,
  type LadderLevelIdentity,
} from '../ValueDashboard/ladder/CancelLevelControl';
import type { MarketSide, PriceMode, VenueBook } from '../ValueDashboard/types';
import type { UserOrderSlice, UserPositionSlice } from '../../hooks/useUserKalshiPOS';

/** Shared venue layout. The price pills and the expanded orderbook both use
 *  it, so Kalshi's prices sit directly above Kalshi's book and Poly's above
 *  Poly's. Capped rather than fluid: these are two short price groups, and
 *  letting them span a wide card only pushes the venues apart. */
export const VENUE_GRID = 'grid max-w-[680px] grid-cols-2 gap-x-6';
/** The second venue column carries the boundary rule. Deliberately not
 *  `border-border` — on the dark theme that sits within a hair of the card
 *  background and a 1px rule disappears entirely. */
export const VENUE_COL_2 = 'border-muted-foreground/25 border-l pl-6';

/** One named outcome — the unit these sections are actually made of. */
export interface CompactOutcome {
  /** Stable unique identity for expand state. NEVER a shared attribute like
   *  a team code or threshold: sibling outcomes collide on those and the
   *  wrong band opens (the stranded-highlight class Brendan caught on #44). */
  key: string;
  /** What the bet is, in words: "Los Angeles Dodgers", "Tatis 2+ Hits". */
  label: string;
  kalshi: VenueBook | null;
  polymarket: VenueBook | null;
  /** Market resolved — dims the band and shows the SETTLED chip. */
  settled?: boolean;
  /** Game is final. Prices stop being tradable quotes, so the pills give way
   *  to a winner tick / dash, matching the detailed table. */
  final?: boolean;
  /** This outcome is the winning one on a final game. */
  winner?: boolean;
}

export interface CompactOutcomeBandsProps {
  outcomes: CompactOutcome[];
  marketSide: MarketSide;
  priceMode: PriceMode;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  /** Key of the expanded band, or null. Controlled by the parent so the
   *  section can coordinate it with the right-hand trade panel. */
  expandedKey: string | null;
  onToggleExpand: (key: string) => void;
  onTrade: (
    outcome: CompactOutcome,
    venue: 'kalshi' | 'polymarket',
    side: MarketSide,
    cents: number | null,
  ) => void;
  /** Maker-rebate fraction, computed once by the section (same input the
   *  detailed rows get) so pill odds agree across both views. */
  makerRebatePct?: number;
  /** Shown when there are no outcomes at all — an explicit sentence beats a
   *  bare empty box, which reads as broken rather than as "nothing yet". */
  emptyMessage?: string;
  /** Opt these bands into the GAME-level A/D hotkey contract (packet §V2B:
   *  A = the visually top team's NO, D = the bottom team's NO). Enabled only
   *  by GameDetail's Moneyline section — the other caller (Game Lines'
   *  moneyline/BTTS compact tabs) stays unwired. The named outcome keys pick
   *  which two bands are the team pair; every other band (the soccer Draw)
   *  stays mouse-only and never registers or inherits a card. */
  lt2Hotkeys?: {
    scope: Lt2MoneylineCardScope;
    gameKey: string;
    awayOutcomeKey: string;
    homeOutcomeKey: string;
  };
}

export function CompactOutcomeBands({
  outcomes,
  marketSide,
  priceMode,
  positionByTicker,
  ordersByTicker,
  expandedKey,
  onToggleExpand,
  onTrade,
  makerRebatePct = 0,
  emptyMessage = 'No open markets yet.',
  lt2Hotkeys,
}: CompactOutcomeBandsProps) {
  // ── GAME-level A/D registration (one card for the away/home pair) ──────
  const lt2Access = useLt2Access();
  const lt2HotkeysEnabled = useDisplaySettings((s) => s.lt2HotkeysEnabled);
  const lt2Armed = useOneClickStore((st) => st.armed);
  const lt2FeeAdjusted = useDisplaySettings((s) => s.feeAdjustedOdds);
  const lt2HideUnknownFees = useDisplaySettings((s) => s.hideUnknownFeePrices);
  const lt2Away = lt2Hotkeys
    ? (outcomes.find((o) => o.key === lt2Hotkeys.awayOutcomeKey) ?? null)
    : null;
  const lt2Home = lt2Hotkeys
    ? (outcomes.find((o) => o.key === lt2Hotkeys.homeOutcomeKey) ?? null)
    : null;
  const lt2AwayTicker = lt2Away?.kalshi?.marketTicker || null;
  const lt2HomeTicker = lt2Home?.kalshi?.marketTicker || null;
  const lt2On = lt2Hotkeys != null && lt2Access && lt2HotkeysEnabled;
  const lt2AwayFeeEntry = useFeeFor(lt2On ? lt2AwayTicker : null);
  const lt2HomeFeeEntry = useFeeFor(lt2On ? lt2HomeTicker : null);
  const lt2AwayFeeStatus = useFeeDisplayStatus(lt2On ? lt2AwayTicker : null, lt2AwayFeeEntry);
  const lt2HomeFeeStatus = useFeeDisplayStatus(lt2On ? lt2HomeTicker : null, lt2HomeFeeEntry);
  const lt2GameCardId = lt2Hotkeys
    ? lt2MoneylineGameCardId(lt2Hotkeys.scope.keyPrefix, lt2Hotkeys.gameKey)
    : null;
  const lt2Live = (o: CompactOutcome | null) =>
    o != null && !o.final && !o.settled && o.kalshi?.marketTicker != null;
  const lt2GameEnabled =
    lt2On && lt2GameCardId != null && lt2Live(lt2Away) && lt2Live(lt2Home);
  const lt2GameFocused = useLt2Card(
    lt2GameEnabled ? lt2GameCardId : null,
    lt2GameEnabled,
    () => {
      if (!lt2Hotkeys || !lt2Away || !lt2Home) return null;
      const hidden = (status: ReturnType<typeof useFeeDisplayStatus>) =>
        lt2FeeAdjusted && lt2HideUnknownFees && feeDisplayCanHide(status);
      return lt2MoneylineGameTargets({
        scope: lt2Hotkeys.scope,
        away: { book: lt2Away.kalshi, label: lt2Away.label, priceHidden: hidden(lt2AwayFeeStatus) },
        home: { book: lt2Home.kalshi, label: lt2Home.label, priceHidden: hidden(lt2HomeFeeStatus) },
        priceMode,
      });
    },
  );

  if (outcomes.length === 0) {
    return (
      <div className="text-muted-foreground px-4 py-6 text-center text-sm">{emptyMessage}</div>
    );
  }

  return (
    <div className="divide-border/60 divide-y">
      {outcomes.map((o) => {
        const slot: 'a' | 'd' | null =
          lt2Hotkeys && o.key === lt2Hotkeys.awayOutcomeKey
            ? 'a'
            : lt2Hotkeys && o.key === lt2Hotkeys.homeOutcomeKey
              ? 'd'
              : null;
        return (
          <OutcomeBand
            key={o.key}
            outcome={o}
            marketSide={marketSide}
            priceMode={priceMode}
            positionByTicker={positionByTicker}
            ordersByTicker={ordersByTicker}
            expanded={expandedKey === o.key}
            onToggleExpand={() => onToggleExpand(o.key)}
            onTrade={onTrade}
            makerRebatePct={makerRebatePct}
            lt2Band={lt2Hotkeys != null}
            lt2Slot={slot}
            lt2GameCardId={lt2GameCardId}
            lt2GameFocused={lt2GameFocused}
            lt2GameEnabled={lt2GameEnabled}
            lt2Armed={lt2Armed}
            lt2BadgeSides={
              lt2Away && lt2Home
                ? `A posts NO on ${lt2Away.label} (top), D posts NO on ${lt2Home.label} (bottom)`
                : ''
            }
          />
        );
      })}
    </div>
  );
}

function OutcomeBand({
  outcome,
  marketSide,
  priceMode,
  positionByTicker,
  ordersByTicker,
  expanded,
  onToggleExpand,
  onTrade,
  makerRebatePct,
  lt2Band,
  lt2Slot,
  lt2GameCardId,
  lt2GameFocused,
  lt2GameEnabled,
  lt2Armed,
  lt2BadgeSides,
}: {
  outcome: CompactOutcome;
  marketSide: MarketSide;
  priceMode: PriceMode;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
  expanded: boolean;
  onToggleExpand: () => void;
  onTrade: CompactOutcomeBandsProps['onTrade'];
  makerRebatePct: number;
  /** These bands are wired for A/D at all (marks every band for tests). */
  lt2Band: boolean;
  /** 'a' = the visually top (away) team's band, 'd' = the bottom (home)
   *  team's. null = not part of the pair — the soccer Draw band, which is
   *  mouse-only: no marks, no chip, no badge, no focus highlight. */
  lt2Slot: 'a' | 'd' | null;
  lt2GameCardId: string | null;
  lt2GameFocused: boolean;
  lt2GameEnabled: boolean;
  lt2Armed: boolean;
  lt2BadgeSides: string;
}) {
  const { kalshi: kBook, polymarket: pBook, settled, final, winner } = outcome;
  const hasAnyBook = kBook != null || pBook != null;
  // The GAME card is registered once by CompactOutcomeBands; a band in the
  // A/D pair only paints that card's marks. Both team bands stay marked while
  // one is expanded — the price line keeps rendering above the inline ladder,
  // so each key's displayed price stays visible (and firable) throughout.
  const lt2BandFocused = lt2GameFocused && lt2Slot != null && lt2GameCardId != null;

  return (
    // Dimmed on final OR settled, matching the detailed row's
    // `(isFinal || settled) && 'opacity-60'` — dimming only on `settled`
    // left a finished game reading as live in Compact but resolved in
    // Detailed.
    <div
      className={cn(
        'px-4 py-2.5',
        (settled || final) && 'opacity-60',
        lt2BandFocused && '!bg-primary/15',
      )}
      data-lt2-band={lt2Band ? outcome.key : undefined}
      data-lt2-focused={lt2BandFocused ? (lt2GameCardId ?? undefined) : undefined}
      data-lt2-price={lt2BandFocused ? (lt2Slot ?? undefined) : undefined}
      data-lt2-hotkey-control={lt2BandFocused || undefined}
    >
      {/* Identity line. The label and chevron are the expand target (Brendan
          okayed a separate chevron / expand target for #105) — the price pills
          sit on their own line below, so a trade click can never be mistaken
          for an expand click. */}
      <div className="flex w-full items-center gap-2">
        <button
          type="button"
          onClick={onToggleExpand}
          disabled={!hasAnyBook}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 text-left transition-colors',
            hasAnyBook ? 'hover:text-foreground' : 'cursor-default',
          )}
          title={hasAnyBook ? 'Show orderbook' : undefined}
        >
          <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
            {outcome.label}
          </span>
          {settled && (
            <span
              className="shrink-0 rounded border border-zinc-500/60 bg-zinc-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-400"
              title="Market resolved — prices are settlement values, not tradable quotes"
            >
              SETTLED
            </span>
          )}
        </button>
        {/* The pre-focus half of the gesture: one ⌨ chip per GAME, on the
            top team's band — aiming targets the game pair, never one band. */}
        {lt2Slot === 'a' &&
          !lt2GameFocused &&
          lt2Armed &&
          lt2GameEnabled &&
          lt2GameCardId && (
            <Lt2FocusGhostChip
              onFocus={() => useLt2FocusStore.getState().setFocus(lt2GameCardId)}
            />
          )}
        {/* Game-level hotkey badge — the visible claim that A posts NO on the
            top team and D posts NO on the bottom team, at their displayed prices. Once,
            on the top band; the Draw band never carries it. */}
        {lt2Slot === 'a' && lt2GameFocused && lt2GameCardId && (
          <Lt2FocusBadge
            sides={lt2BadgeSides}
            onClear={() => useLt2FocusStore.getState().clearFocusIf(lt2GameCardId)}
          />
        )}
        <PosChips
          book={kBook}
          positionByTicker={positionByTicker}
          ordersByTicker={ordersByTicker}
        />
        {hasAnyBook && (
          <button
            type="button"
            onClick={onToggleExpand}
            className="text-muted-foreground shrink-0 text-[10px]"
            aria-label={expanded ? 'Hide orderbook' : 'Show orderbook'}
          >
            <span
              className={cn('block transition-transform', expanded && 'rotate-180')}
              aria-hidden
            >
              ▼
            </span>
          </button>
        )}
      </div>

      {/* Price line — both venues, always both rendered. A venue with no
          book shows disabled pills rather than vanishing, so "Poly doesn't
          list this" stays visible instead of looking like a layout bug.
          The two groups carry named labels and a divider between them:
          venue has to survive the density switch, and a bare "K"/"P" next
          to four near-identical pills does not read as a venue boundary.

          Laid out on VENUE_GRID — the same grid the expanded orderbook
          uses — so a venue's prices and its book sit in one column. Content
          sizing put the Poly book under the gap between the two pill groups,
          and it also let the pills shift as prices ticked, since '+122.2' is
          wider than '−99.1'. Fixed columns fix both (cf. the detailed
          table's reserved widths, GameTable.tsx). */}
      <div className={cn('mt-1.5', VENUE_GRID)}>
        <VenuePills
          venueLabel="KALSHI"
          book={kBook}
          venue="kalshi"
          outcome={outcome}
          marketSide={marketSide}
          priceMode={priceMode}
          onTrade={onTrade}
          makerRebatePct={makerRebatePct}
          final={final}
          winner={winner}
        />
        <div className={VENUE_COL_2}>
          <VenuePills
            venueLabel="POLY"
            book={pBook}
            venue="polymarket"
            outcome={outcome}
            marketSide={marketSide}
            priceMode={priceMode}
            onTrade={onTrade}
            makerRebatePct={makerRebatePct}
            final={final}
            winner={winner}
          />
        </div>
      </div>

      {expanded && hasAnyBook && (
        <CompactLadder
          kBook={kBook}
          pBook={pBook}
          marketSide={marketSide}
          ordersByTicker={ordersByTicker}
        />
      )}
    </div>
  );
}

/** One venue's Y/N pair. Prices come from the shared pillPriceCents so a
 *  band and a detailed row can never quote different numbers. */
function VenuePills({
  venueLabel,
  book,
  venue,
  outcome,
  marketSide,
  priceMode,
  onTrade,
  makerRebatePct,
  final,
  winner,
}: {
  venueLabel: string;
  book: VenueBook | null;
  venue: 'kalshi' | 'polymarket';
  outcome: CompactOutcome;
  marketSide: MarketSide;
  priceMode: PriceMode;
  onTrade: CompactOutcomeBandsProps['onTrade'];
  makerRebatePct: number;
  final?: boolean;
  winner?: boolean;
}) {
  const yesCents = pillPriceCents(book, 'yes', priceMode);
  const noCents = pillPriceCents(book, 'no', priceMode);
  // #167 — crossed-book tripwire. Book-level and side-symmetric, so one read
  // qualifies both pills; derived per render, so it clears itself when the
  // book heals or ownership falls back to a sane source.
  const crossed = crossedBookRead(book, venue);

  return (
    <div className="flex items-center gap-1.5">
      <span className="text-muted-foreground w-12 shrink-0 text-[10px] font-semibold uppercase tracking-wider">
        {venueLabel}
      </span>
      {final ? (
        // Resolved: 100¢/0¢ pills are noise, so the table swaps them for a
        // winner tick. Same treatment here or the two views disagree about
        // what a finished market looks like.
        <span className="text-sm" title={winner ? 'Resolved YES (winner)' : 'Did not win'}>
          {winner ? (
            <span className="text-positive font-semibold">✓</span>
          ) : (
            <span className="text-muted-foreground/40">—</span>
          )}
        </span>
      ) : (
        <>
          <PricePill
            label="Y"
            cents={yesCents}
            side="yes"
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={book == null || book.freshness === 'stale'}
            ticker={book?.marketTicker ?? null}
            makerRebatePct={makerRebatePct}
            crossedBook={crossed}
            onClick={(e) => {
              e.stopPropagation();
              onTrade(outcome, venue, 'yes', yesCents);
            }}
          />
          <PricePill
            label="N"
            cents={noCents}
            side="no"
            activeMarketSide={marketSide}
            priceMode={priceMode}
            disabled={book == null || book.freshness === 'stale'}
            ticker={book?.marketTicker ?? null}
            makerRebatePct={makerRebatePct}
            crossedBook={crossed}
            onClick={(e) => {
              e.stopPropagation();
              onTrade(outcome, venue, 'no', noCents);
            }}
          />
        </>
      )}
    </div>
  );
}

/** Position + open-order rings, Kalshi-only — matching the detailed table's
 *  PosCell, which is also Kalshi-only (Poly orders aren't tracked). Renders
 *  nothing when you have no exposure, so the band stays quiet by default. */
function PosChips({
  book,
  positionByTicker,
  ordersByTicker,
}: {
  book: VenueBook | null;
  positionByTicker: Map<string, UserPositionSlice>;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}) {
  if (!book) return null;
  const pos = positionByTicker.get(book.marketTicker);
  const orders = ordersByTicker.get(book.marketTicker);
  const hasPos = pos != null && pos.yesCount + pos.noCount > 0;
  const hasOrders = orders != null && orders.length > 0;
  if (!hasPos && !hasOrders) return null;

  let yesOrders = 0;
  let noOrders = 0;
  for (const o of orders ?? []) {
    if (o.side === 'yes') yesOrders += 1;
    else noOrders += 1;
  }

  return (
    <span className="flex shrink-0 items-center gap-1">
      {hasPos && (
        <span className="inline-flex items-center" title={heldContractsTitle(pos!)}>
          <PosRing yesCount={pos!.yesCount} noCount={pos!.noCount} variant="closed" size={16} />
        </span>
      )}
      {/* Open orders. The tooltip is the same per-order text the detailed
          table's PosCell builds — one line per resting order with its price,
          size and queue — rather than a bare count. */}
      {hasOrders && (
        <span
          className="inline-flex items-center"
          title={orders!.map((o) => orderTooltip(o, book)).join('\n')}
        >
          <PosRing yesCount={yesOrders} noCount={noOrders} variant="open" size={16} />
        </span>
      )}
    </span>
  );
}

/** The expand-to-ladder payload: 3 asks → last-trade strip → 3 bids, per
 *  venue, side by side. Book data comes from compactLadder.ts, which shares
 *  the detailed ladder's primitives. */
function CompactLadder({
  kBook,
  pBook,
  marketSide,
  ordersByTicker,
}: {
  kBook: VenueBook | null;
  pBook: VenueBook | null;
  marketSide: MarketSide;
  ordersByTicker: Map<string, UserOrderSlice[]>;
}) {
  const k = buildCompactLadderSide(kBook, marketSide);
  const p = buildCompactLadderSide(pBook, marketSide);
  // Your resting orders, by price level. Buys rest on the bid stack, sells
  // on the ask stack — same split the detailed ladder makes.
  const kBuys = buildCompactOrdersByCents(kBook, ordersByTicker, marketSide, 'buy');
  const kSells = buildCompactOrdersByCents(kBook, ordersByTicker, marketSide, 'sell');

  return (
    // Tinted so the book reads as belonging to THIS band rather than
    // floating between two of them — but on the SAME VENUE_GRID as the price
    // pills above, and with no left indent, so each venue's book sits under
    // its own prices instead of drifting into the gap between them.
    <div className={cn('bg-muted/20 mt-2 rounded py-2', VENUE_GRID)}>
      <LadderColumn
        title="Kalshi"
        side={k}
        ordersForAsks={kSells}
        ordersForBids={kBuys}
        orderSide={marketSide}
        book={kBook}
      />
      <div className={VENUE_COL_2}>
        <LadderColumn title="Polymarket" side={p} book={pBook} />
      </div>
    </div>
  );
}

function LadderColumn({
  title,
  side,
  ordersForAsks,
  ordersForBids,
  orderSide,
  book,
}: {
  title: string;
  side: ReturnType<typeof buildCompactLadderSide>;
  ordersForAsks?: Map<number, BidRowOrder[]>;
  ordersForBids?: Map<number, BidRowOrder[]>;
  /** Side the own orders were bucketed by. Only the Kalshi column carries
   *  own orders, so Polymarket leaves this (and the level control) unset. */
  orderSide?: MarketSide;
  book: VenueBook | null;
}) {
  // Three DIFFERENT empty states that must not look alike, or the book reads
  // as broken when it's actually just quiet:
  //   • no book object      → the venue doesn't list this market at all
  //   • book, but no levels → the venue lists it and quotes a price, but
  //     publishes no depth. Every Polymarket PROP is this: static Gamma
  //     snapshots (~120s), never WS-subscribed for depth.
  //   • levels, some blank  → a genuinely thin book; the '·' rungs are real
  const hasDepth = book != null && (book.yes.length > 0 || book.no.length > 0);
  return (
    // Width comes from VENUE_GRID's column, so this lines up with the venue's
    // price pills above it.
    <div className="font-mono text-[11px]">
      <div className="text-muted-foreground/70 mb-1 text-[10px] font-semibold uppercase tracking-wider">
        {title}
      </div>
      {book == null ? (
        <div className="text-muted-foreground/50 py-1">Not listed</div>
      ) : !hasDepth ? (
        <div className="text-muted-foreground/50 py-1">
          No orderbook
          <span className="text-muted-foreground/40 block text-[10px]">
            venue quotes a price only
          </span>
        </div>
      ) : (
        <>
          {side.asks.map((lvl, i) => (
            <LadderLine
              key={`a${i}`}
              level={lvl}
              tone="ask"
              // Label the stack once, on its first row. The detailed ladder
              // labels every row; here the stacks are only 3 deep and the
              // column is narrow, so one marker per stack says the same
              // thing without repeating it six times. What matters is that
              // ask-vs-bid isn't carried by red/green alone.
              showLabel={i === 0}
              orders={ordersForAsks?.get(lvl?.priceCents ?? -1)}
              levelIdentity={
                lvl && orderSide && book
                  ? {
                      marketTicker: book.marketTicker,
                      side: orderSide,
                      // The ask stack is where SELL orders rest.
                      action: 'sell',
                      priceCents: lvl.priceCents,
                    }
                  : undefined
              }
            />
          ))}
          {/* Last trade — a real rule across the column, so it reads as the
              boundary between the ask stack and the bid stack instead of a
              number floating in the gap. Em-dash when the venue has never
              printed; never a borrowed price from the other venue. */}
          <div className="text-muted-foreground my-1 flex items-center gap-2 text-[10px]">
            <span className="bg-border h-px flex-1" />
            <span className="shrink-0" title="Last trade">
              {side.lastCents != null ? `${side.lastCents}¢` : '—'}
            </span>
            <span className="bg-border h-px flex-1" />
          </div>
          {side.bids.map((lvl, i) => (
            <LadderLine
              key={`b${i}`}
              level={lvl}
              tone="bid"
              showLabel={i === 0}
              orders={ordersForBids?.get(lvl?.priceCents ?? -1)}
              levelIdentity={
                lvl && orderSide && book
                  ? {
                      marketTicker: book.marketTicker,
                      side: orderSide,
                      // The bid stack is where BUY orders rest.
                      action: 'buy',
                      priceCents: lvl.priceCents,
                    }
                  : undefined
              }
            />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * #650 — how many own-order quantities the compact band prints before it
 * summarises the rest. Lower than the detailed ladder's chip cap because this
 * cell is narrower and the values are bare numbers with no cancel affordance
 * attached, so there is less to gain from showing one more.
 */
const COMPACT_BAND_QTY_LIMIT = 3;

function LadderLine({
  level,
  tone,
  showLabel = false,
  orders,
  levelIdentity,
}: {
  level: { priceCents: number; size: number } | null;
  tone: 'ask' | 'bid';
  showLabel?: boolean;
  orders?: BidRowOrder[];
  /** #523 — the exact level these own orders rest at, when the column carries
   *  own orders at all. Present ⇒ the row offers the level-cancel control. The
   *  qty readout beside it stays read-only; per-order cancel chips are the
   *  detailed ladder's affordance and have not been brought over here. */
  levelIdentity?: LadderLevelIdentity;
}) {
  // The label column is reserved on EVERY row, labelled or not, so the price
  // column stays aligned down the stack.
  const label = (
    <span className="text-muted-foreground/70 w-7 shrink-0 text-[9px] font-semibold uppercase tracking-wider">
      {showLabel ? (tone === 'ask' ? 'Ask' : 'Bid') : ''}
    </span>
  );
  if (!level) {
    return (
      <div className="flex items-baseline gap-3 py-px">
        {label}
        <span className="text-muted-foreground/25 w-10 text-right">·</span>
      </div>
    );
  }
  return (
    <div className="flex items-baseline gap-3 py-px">
      {label}
      <span className={cn('w-10 text-right font-semibold', tone === 'ask' ? 'text-negative' : 'text-positive')}>
        {level.priceCents}¢
      </span>
      <span className="text-muted-foreground w-12 text-right">{formatVolume(level.size)}</span>
      {orders && orders.length > 0 && (
        // The level control LEADS the trader's own-order group here exactly as
        // it does on the two detailed ladders, so the gesture sits in the same
        // place relative to the quantities on every surface.
        <>
          {levelIdentity && (
            <span className="text-[10px]" onClick={(e) => e.stopPropagation()}>
              <CancelLevelControl level={levelIdentity} orders={orders} />
            </span>
          )}
          {/* #650 — bounded, and honest about what it is not showing.
              This list was a space-joined string under a bare `truncate`, so
              past a few orders it simply ran out of room and the rest vanished
              with nothing to say they existed — the same silent drop the
              detailed ladders had, on the one surface that cannot offer a
              per-order cancel to recover them (that stays the detailed
              ladder's affordance, deliberately, and is not widened here).
              Showing a count keeps the existence of the remainder visible;
              the tooltip still carries every order in full. */}
          <span
            className="text-foreground/80 whitespace-nowrap text-[10px] leading-4"
            title={orders.map((o) => o.tooltip).join('\n')}
          >
            {orders
              .slice(0, COMPACT_BAND_QTY_LIMIT)
              .map((o) => Math.round(o.remainingCount))
              .join(' ')}
            {orders.length > COMPACT_BAND_QTY_LIMIT && (
              <span className="text-muted-foreground">
                {' '}
                +{orders.length - COMPACT_BAND_QTY_LIMIT}
              </span>
            )}
          </span>
        </>
      )}
    </div>
  );
}
